import type { FC } from 'react'
import { useCallback, useMemo } from 'react'

import { Divider } from 'antd'
import classNames from 'classnames'
import dayjs from 'dayjs'

import { useAppDispatch, useAppSelector } from 'common/hooks/redux'
import type { ISiteRoom } from 'common/interfaces/ISiteRoom'
import { ALT_CONSTANTS } from 'common/constants/altConstants'
import { UtilService } from 'common/services/utilService'

import trashIcon from 'features/Cart/assets/trash.svg'
import { CartCollapse } from 'features/Cart/components/CartCollapse/CartCollapse'
import collapseStyles from 'features/Cart/components/CartCollapse/cartCollapse.module.scss'
import { CartError } from 'features/Cart/components/CartError/CartError'
import { CartItem } from 'features/Cart/components/CartItem/CartItem'
import shopItemStyles from 'features/Cart/components/CartShopItem/cartShopItem.module.scss'
import { CART_CONSTANTS } from 'features/Cart/constants/cartConstants'
import { OPTION_TYPE_CONSTANTS } from 'features/Option/constants/optionContants'
import type {
  IOptionShopCartItem,
  IOptionShopCartSlotExtension,
  ISlotExtensionRoom,
} from 'features/Option'
import { OptionSlotExtensionService } from 'features/Option'
import { useShopCartManager } from 'features/Option/hooks/useShopCartManager'
import { UPDATE_OPTION_SHOP_ITEMS } from 'features/Option/state/slices/optionSlice'

import styles from './cartShopExtension.module.scss'

const { numberToDollar } = UtilService

const { removeRoomExtension, removeEntireSlotExtension } = OptionSlotExtensionService

const parseExtensionLineDetails = (name: string): { name: string; slot: string } => {
  const wordList: string[] = name.split('|')
  const slot: string = wordList.at(1)?.split('$').at(0) ?? ''
  return { name: wordList.at(0) ?? '', slot }
}

const groupPackageExtensionRooms = (
  rooms: ISlotExtensionRoom[],
): { groupTitle: string; rooms: ISlotExtensionRoom[] }[] => {
  const byTitle = new Map<string, ISlotExtensionRoom[]>()
  const order: string[] = []

  for (const room of rooms) {
    const { name: roomTitle } = parseExtensionLineDetails(room.name)
    const key = roomTitle.trim()
    if (!byTitle.has(key)) {
      order.push(key)
      byTitle.set(key, [])
    }
    byTitle.get(key)!.push(room)
  }

  return order.map((groupTitle) => {
    const groupRooms = byTitle.get(groupTitle)!
    groupRooms.sort((a, b) => dayjs(a.date || 0).valueOf() - dayjs(b.date || 0).valueOf())
    return { groupTitle, rooms: groupRooms }
  })
}

interface IProps {
  bookingId: string
  handleEdit: (option: ISiteRoom) => void
  slotExtension: IOptionShopCartSlotExtension | null
}

export const CartShopExtension: FC<IProps> = ({ handleEdit, slotExtension, bookingId }) => {
  const dispatch = useAppDispatch()
  const { updateShopCartContent } = useShopCartManager()

  const { optionShopItems } = useAppSelector((state) => state.optionReducer)

  const isPackageExtension =
    slotExtension?.option.price_type === OPTION_TYPE_CONSTANTS.PACKAGE_EXTENSION

  const packageRoomGroups = useMemo(
    () =>
      slotExtension && isPackageExtension ? groupPackageExtensionRooms(slotExtension.rooms) : [],
    [slotExtension, isPackageExtension],
  )

  const handleRemoveSlot = useCallback(
    (roomCode: number): void => {
      const updatedShopItems: IOptionShopCartItem[] = removeRoomExtension(
        bookingId,
        roomCode,
        optionShopItems,
      )
      dispatch(UPDATE_OPTION_SHOP_ITEMS(updatedShopItems))

      updateShopCartContent(updatedShopItems)
    },
    [optionShopItems],
  )

  const handleRemoveEntirePackageExtension = useCallback((): void => {
    const updatedShopItems: IOptionShopCartItem[] = removeEntireSlotExtension(
      bookingId,
      optionShopItems,
    )
    dispatch(UPDATE_OPTION_SHOP_ITEMS(updatedShopItems))
    void updateShopCartContent(updatedShopItems)
  }, [bookingId, optionShopItems, dispatch, updateShopCartContent])

  if (!slotExtension) return null

  return (
    <>
      <CartItem
        price={slotExtension.amount}
        key={slotExtension.option.id}
        name={slotExtension.option.name}
        img={slotExtension.option.images.at(0)}
        handleEdit={() => handleEdit(slotExtension.option)}
        handleDelete={
          isPackageExtension ? handleRemoveEntirePackageExtension : undefined
        }>
        {isPackageExtension
          ? packageRoomGroups.map(
              ({ groupTitle, rooms: groupRooms }): React.JSX.Element => {
                const groupKey = `${groupTitle}-${groupRooms.map((r) => r.code).join('-')}`
                const groupTotal = groupRooms.reduce((sum, r) => sum + r.price, 0)

                return (
                  <CartCollapse
                    key={groupKey}
                    title={groupTitle}
                    additionalActiveTitle={`${groupRooms.length} slot${
                      groupRooms.length === 1 ? '' : 's'
                    }`}>
                    <div>
                      {groupRooms.map((room: ISlotExtensionRoom): React.JSX.Element => {
                        const { slot } = parseExtensionLineDetails(room.name)
                        return (
                          <div key={room.code} className={shopItemStyles.divider}>
                            <div
                              className={classNames(shopItemStyles.parent, shopItemStyles.slotItem)}>
                              <div className={styles.packageSlotTitle}>
                                {room.date ? (
                                  <span className={styles.packageSlotDate}>
                                    {dayjs(room.date).format("ddd, MMM DD 'YY")}
                                  </span>
                                ) : null}
                                <span>{slot}</span>
                              </div>
                              <div className={styles.packageSlotEnd}>
                                <span>{numberToDollar(room.price)}</span>
                                <button
                                  type='button'
                                  className={classNames(
                                    collapseStyles.parentRemoveButton,
                                    styles.packageSlotRemoveBtn,
                                  )}
                                  aria-label='Remove slot'
                                  title='Remove slot'
                                  onClick={() => handleRemoveSlot(room.code)}>
                                  <img
                                    src={trashIcon}
                                    alt={ALT_CONSTANTS.ACTION_ICON}
                                    className={collapseStyles.parentRoomRemoveIcon}
                                  />
                                </button>
                              </div>
                            </div>
                            <CartError error={room.error_message} className={shopItemStyles.slotItem} />
                          </div>
                        )
                      })}
                      <Divider className={shopItemStyles.divider} />
                      <div className={classNames(shopItemStyles.parent, shopItemStyles.grandTotal)}>
                        <span>Grand Total</span>
                        <span>{numberToDollar(groupTotal)}</span>
                      </div>
                    </div>
                  </CartCollapse>
                )
              },
            )
          : slotExtension.rooms.map((room: ISlotExtensionRoom, index: number): React.JSX.Element => {
              const { name, slot } = parseExtensionLineDetails(room.name)
              return (
                <CartCollapse
                  key={index}
                  title={name}
                  error={room?.error_message}
                  additionalActiveTitle={slot}
                  handleRemoveSlot={() => handleRemoveSlot(room.code)}>
                  <div className={styles.parent}>
                    {CART_CONSTANTS.QUANTITY} 1 x {numberToDollar(room.price)}
                    <span>{numberToDollar(room.price)}</span>
                  </div>
                </CartCollapse>
              )
            })}
      </CartItem>
      <hr />
    </>
  )
}
